import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class FiltersService {
  private perm;
  private _predefinedSearch: BehaviorSubject<any[]>;

  private dataStore: {
    predefinedSearch: any[]
  }

  get predefinedSearch(): Observable<any[]> {
    return this._predefinedSearch.asObservable();
  }

  constructor(
    private http: HttpClient,
    private utilsService: UtilsService,
    private ngxPermissionsService: NgxPermissionsService,
    private toastr: ToastrService
  ) {
    this.perm = this.ngxPermissionsService.getPermissions();
    this.dataStore = { predefinedSearch: [] };
    this._predefinedSearch = new BehaviorSubject<any[]>([]);
  }

  getCustomers() {
    return this.http.get(`${environment.apiUrl}/api/filters/customers`);
  }

  getSalesOrg() {
    return this.http.get<any[]>(`${environment.apiUrl}/api/filters/sales_org`).pipe(map(data => {
      let dataset = data;

      if (this.perm['gip_client']) {
        dataset = data.map(i => ({ ...i, key: i.description, description: this.utilsService.getSoDesc(i.description) }));
      }

      return dataset;
    }));
  }

  getStates() {
    return this.http.get(`${environment.apiUrl}/api/filters/states`);
  }

  getSubBu() {
    return this.http.get(`${environment.apiUrl}/api/filters/subbusinessunits`);
  }

  getBu() {
    return this.http.get(`${environment.apiUrl}/api/filters/businessunits`);
  }

  getSegments() {
    return this.http.get(`${environment.apiUrl}/api/filters/segments`);
  }

  getPredefinedFilters() {
    return this.http.get(`${environment.apiUrl}/api/filters/customsearchs`)
      .subscribe((data: any) => {
        const dataset = data.map(f => {
          if (this.perm['gip_client']) {
            f.filters.customer = f.filters.customer ? f.filters.customer.split('|') : null;
            f.filters.legal_inv_num = f.filters['legal_Inv_Num'];
            f.filters.sales_order = f.filters['sales_Order'];
            f.filters.rig_well_afe = f.filters['rig_Well_Afe'];
            f.filters.po = f.filters['po'];
            f.filters.sales_organization = f.filters['sales_Organization'] ? f.filters['sales_Organization'].split('|') : null;
          }
          else {
            f.filters.customer = f.filters.customer ? f.filters.customer.split('|') : null;
            f.filters.status = f.filters.status ? f.filters.status.split('|') : null;
            f.filters.inv_num = f.filters['inv_Num'];
            f.filters.sales_order = f.filters['sales_Order'];
            f.filters.sales_organization = f.filters['sales_Organization'] ? f.filters['sales_Organization'].split('|') : null;
          }

          return f;
        });

        this.dataStore.predefinedSearch = dataset;
        this._predefinedSearch.next(Object.assign({}, this.dataStore).predefinedSearch);
      }, err => { this.toastr.error(err); });
  }

  retrieveInvoiceFilters(): any {
    let customers = this.getCustomers();
    let salesOrg = this.getSalesOrg();
    let states = this.getStates();
    return forkJoin([customers, salesOrg, states]);
  }

  retrieveCustomersFilters(): any {
    let salesOrg = this.getSalesOrg();
    let bu = this.getBu();
    let subBu = this.getSubBu();
    let segments = this.getSegments();
    let customers = this.getCustomers();
    return forkJoin([salesOrg, bu, subBu, segments, customers]);
  }

  formatPredefinedSearchObj(predefinedSearch, filters, add = false) {
    const f = { ...filters };
    let body = Object.assign({}, predefinedSearch);

    body.filters = f;

    if (add) delete body.id;
    delete body.filters.range;
    body.filters.customer = body.filters.customer && body.filters.customer.length ? body.filters.customer.join('|') : null;
    body.filters.status = body.filters.status && body.filters.status.length ? body.filters.status.join('|') : null;
    body.filters.sales_organization = body.filters.sales_organization && body.filters.sales_organization.length ? body.filters.sales_organization.join('|') : null;
    return body;
  }

  addPredefinedSearch(predefinedSearch, filters) {
    let body = this.formatPredefinedSearchObj(predefinedSearch, filters, true);
    return this.http.post(`${environment.apiUrl}/api/filters/save`, body).pipe(
      map(data => {
        predefinedSearch.id = data['id'];
        predefinedSearch.filters = filters;
        return { predefinedSearch };
      })
    )
  }

  updatePredefinedSearch(predefinedSearch, filters) {
    let body = this.formatPredefinedSearchObj(predefinedSearch, filters);
    return this.http.post(`${environment.apiUrl}/api/filters/update`, body).pipe(
      map(data => {
        predefinedSearch.filters = filters;
        return { predefinedSearch };
      })
    )
  }

  deletePredefinedSearch(id) {
    return this.http.get(`${environment.apiUrl}/api/filters/delete/${id}`);
  }

  updatePredefinedSearchStore(action, element) {
    switch (action) {
      case 'add':
        if (element.default) {
          this.dataStore.predefinedSearch = this.dataStore.predefinedSearch.map(f => {
            if (f.default) f.default = false;
            return f;
          });
        }
        this.dataStore.predefinedSearch.push(element);
        this._predefinedSearch.next(Object.assign({}, this.dataStore).predefinedSearch);
        break;
      case 'edit':
        this.dataStore.predefinedSearch = this.dataStore.predefinedSearch.map(f => {
          if (element.default && f.default) f.default = false;
          return f.id === element.id ? element : f;
        });
        this._predefinedSearch.next(Object.assign({}, this.dataStore).predefinedSearch);
        break;
      case 'delete':
        this.dataStore.predefinedSearch = this.dataStore.predefinedSearch.filter(f => f.id !== element);
        this._predefinedSearch.next(Object.assign({}, this.dataStore).predefinedSearch);
        break;
    }
  }
}
